import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Palette, Save, ArrowLeft } from 'lucide-react'
import { settingsAPI } from '@/lib/api'
import PageHeader from '@/components/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

export default function BrandingSettingsPage() {
  const [form, setForm] = useState({ site_name: '', site_subtitle: '', footer_text: '', brand_mark: '', brand_logo_url: '' })
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    settingsAPI.getBranding().then(r => setForm(r.data.branding)).catch(() => {})
  }, [])

  const save = async () => {
    setSaving(true)
    setMsg('')
    try {
      await settingsAPI.setBranding(form)
      try { sessionStorage.setItem('brandingCache', JSON.stringify(form)) } catch {}
      setMsg('Opgeslagen. Refresh de pagina om het overal te zien.')
    } catch (e) {
      setMsg('Fout: ' + (e.response?.data?.error || e.message))
    } finally { setSaving(false) }
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const uploadLogo = async (file) => {
    if (!file) return
    setUploading(true); setMsg('')
    try {
      const r = await settingsAPI.uploadBrandingLogo(file)
      const url = r.data?.brand_logo_url
      if (url) { set('brand_logo_url', url); try { const cur = JSON.parse(sessionStorage.getItem('brandingCache')||'{}'); sessionStorage.setItem('brandingCache', JSON.stringify({ ...cur, brand_logo_url: url })) } catch {} }
      setMsg('Logo geüpload. Klik Opslaan om te bevestigen.')
    } catch (e) {
      setMsg('Upload-fout: ' + (e.response?.data?.error || e.message))
    } finally { setUploading(false) }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Palette}
        eyebrow="Beheer · site"
        title="Branding"
        subtitle="Sitenaam, subtitel en footer. Kies wat past bij deze LAN party."
        actions={<Button asChild variant="outline"><Link to="/admin"><ArrowLeft className="h-4 w-4 mr-2" />Terug</Link></Button>}
      />

      <Card>
        <CardContent className="p-5 space-y-4">
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Sitenaam</span>
            <input value={form.site_name || ''} onChange={e => set('site_name', e.target.value)}
                   maxLength={60}
                   className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
                   placeholder="Bijv. Bear & Bockie LAN" />
            <span className="text-xs text-muted-foreground">Verschijnt in de sidebar (initialen) en de browser-tab.</span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Subtitel</span>
            <input value={form.site_subtitle || ''} onChange={e => set('site_subtitle', e.target.value)}
                   maxLength={60}
                   className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
                   placeholder="Bijv. editie 13 · maart 26" />
            <span className="text-xs text-muted-foreground">Kleine mono-tekst onder de brand-mark.</span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Footer</span>
            <input value={form.footer_text || ''} onChange={e => set('footer_text', e.target.value)}
                   maxLength={200}
                   className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
                   placeholder="Bijv. Bear & Bockie LAN · thuisserver op Proxmox · sess. 13" />
            <span className="text-xs text-muted-foreground">Onderaan elke pagina.</span>
          </label>

          <div className="border-t border-border/40 pt-4 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Merkteken in de sidebar</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs">Afkorting (max 4)</span>
                <input value={form.brand_mark || ''}
                       onChange={e => set('brand_mark', e.target.value.slice(0, 4))}
                       maxLength={4}
                       className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm font-bold text-acid"
                       placeholder="KL" />
                <span className="text-xs text-muted-foreground">Overschrijft auto-initials.</span>
              </label>
              <div className="flex flex-col gap-1">
                <span className="text-xs">Of logo (PNG/SVG, max 2MB)</span>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden"
                         onChange={e => uploadLogo(e.target.files?.[0])} />
                  <div className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm flex items-center justify-center hover:border-acid transition-colors">
                    {uploading ? 'Uploaden…' : (form.brand_logo_url ? 'Vervang logo' : 'Kies logo')}
                  </div>
                </label>
                {form.brand_logo_url && (
                  <div className="flex items-center gap-2 mt-1">
                    <img src={form.brand_logo_url} alt="" className="h-8 w-8 object-contain rounded" />
                    <button type="button" onClick={() => set('brand_logo_url', '')}
                            className="text-xs text-signal hover:underline">Verwijder</button>
                  </div>
                )}
                <span className="text-xs text-muted-foreground">Overschrijft afkorting. Wordt op ~40px getoond.</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-border/40">
            {msg && <span className="text-xs text-muted-foreground">{msg}</span>}
            <Button onClick={save} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />Opslaan
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border" style={{ borderLeft: '2px solid rgb(var(--peach-rgb))' }}>
        <CardContent className="p-4 text-sm text-muted-foreground">
          <b className="text-peach">Tip:</b> LAN edities beheer je via <Link to="/admin/lan-edities" className="text-acid underline">LAN edities</Link>.
          De branding is stabieler — pas hem aan voor structurele hernoemingen (bijv. andere LAN-community).
        </CardContent>
      </Card>
    </div>
  )
}
